import {
  CameraSettingsRotate1Bold,
  ModernTV3DSyncBold,
  PictureSunBold,
  PinLocation1Bold,
  RealEstateDimensionsBlockBold,
  VideoPlayerSliderBold,
} from '@ha/icons';
import React from 'react';
import { Coords } from 'google-map-react';

import { TranslateFunc } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { Dialog } from '@hbf/dsl';
import { GalleryContainer as Gallery } from 'ha/components/Gallery/containers/Gallery';
import { Slides } from 'ha/components/Gallery/components/Slides';
import { Picture } from 'ha/components/Gallery/components/Picture';
import { PanoramicPicturesContainer } from 'ha/components/Gallery/components/PanoramicPicturesContainer';
import { Video } from 'ha/components/Gallery/components/Video';
import { useCurrentScreenSize } from 'ha/modules/UI/helpers/CurrentScreenSize';
import { ListingPhoto } from '@ha/api/v2/types/Listing';

import { ImgixSize } from 'ha/modules/Image/constants';

import { useImgix } from 'ha/modules/Image/useImgix';
import { GalleryImgixSizeByScreenSize } from '../constants';
import { GalleryMobileLayout } from './GalleryMobileLayout';
import { GalleryDesktopLayout } from './GalleryDesktopLayout';
import { GalleryMap } from './GalleryMap';
import { Video as VideoType, GalleryContentType } from '../types';
import { getMatterportLink } from '../helpers/getMatterportLink';

export interface Props {
  photos: ListingPhoto[];
  floorplanPhotos: ListingPhoto[];
  panoramicPhotos: ListingPhoto[];
  videos: VideoType[];
  isFullscreenGalleryOpen: boolean;
  initialSlideIndex: number;
  initialContentName?: string;
  onClose: () => void;
  onImageButtonClick: () => void;
  onPanoramicPhotosButtonClick: () => void;
  onVideoButtonClick: () => void;
  onFloorplanButtonClick: () => void;
  onMapButtonClick: () => void;
  onVirtualTourButtonClick: () => void;
  street: string;
  country: string;
  city: string;
  listingId: number;
  imageGridProps: {
    tiles: ListingPhoto[];
    onImageClick: (id: number) => void;
  };
  hasCoordinates: boolean;
  coordinates: Coords;
  description: string;
}

/**
 * Returns content for Map
 */
const getMapContent = (T: TranslateFunc, coordinates: Coords) => ({
  slides: <GalleryMap coordinates={coordinates} />,
  total: 1,
  withControls: false,
  withThumbnails: false,
  withSlideIndex: false,
  thumbnails: [],
  navigation: {
    text: T('listings.media.map'),
    icon: <PinLocation1Bold data-test-locator="Gallery/Map" />,
  },
  name: GalleryContentType.Map,
});

const getVirtualTourContent = (T: TranslateFunc, virtualTourLink: string) => ({
  slides: (
    <iframe
      title={T('listing_gallery_3D_tour')}
      width="100%"
      height="90%"
      style={{ marginTop: '2.5rem' }}
      src={`${virtualTourLink}&brand=0`}
      frameBorder="0"
      allowFullScreen
      allow="xr-spatial-tracking"
    />
  ),
  total: 1,
  withControls: false,
  withThumbnails: false,
  withSlideIndex: false,
  thumbnails: [],
  navigation: {
    text: T('listing_gallery_3D_tour'),
    icon: <ModernTV3DSyncBold data-test-locator="Gallery/Virtual Tour" />,
  },
  name: GalleryContentType.VirtualTour,
});

const getPhotosContent = (
  T: TranslateFunc,
  photos: ListingPhoto[],
  thumbnails: string[],
  md: boolean,
) => ({
  slides: (
    <Slides>
      {({ index }) => <Picture key={index} src={photos[index]?.url} />}
    </Slides>
  ),
  total: photos.length,
  withControls: md && photos.length > 1,
  thumbnails,
  navigation: {
    text: T('listings.media.photo'),
    icon: <PictureSunBold data-test-locator="Gallery/Photo" />,
  },
  name: GalleryContentType.Photo,
});

const getPanoramicPhotosContent = (
  T: TranslateFunc,
  photos: ListingPhoto[],
  thumbnails: string[],
) => ({
  slides: <PanoramicPicturesContainer images={photos} />,
  total: photos.length,
  withControls: photos.length > 1,
  thumbnails,
  navigation: {
    text: T('listings.media.panoramic_photo'),
    icon: (
      <CameraSettingsRotate1Bold data-test-locator="Gallery/PanoramicPhoto" />
    ),
  },
  name: GalleryContentType.PanoramicPhoto,
});

const getFloorplansContent = (
  T: TranslateFunc,
  photos: ListingPhoto[],
  thumbnails: string[],
  md: boolean,
) => ({
  slides: (
    <Slides>
      {({ index }) => (
        <Picture key={index} src={photos[index] && photos[index].url} />
      )}
    </Slides>
  ),
  total: photos.length,
  withControls: md && photos.length > 1,
  thumbnails,
  navigation: {
    text: T('listings.media.floorplan'),
    icon: (
      <RealEstateDimensionsBlockBold data-test-locator="Gallery/Floorplan" />
    ),
  },
  name: GalleryContentType.Floorplan,
});

const getVideosContent = (
  T: TranslateFunc,
  videos: VideoType[],
  thumbnails: string[] = [],
) => ({
  slides: (
    <Slides>
      {({ index }) => {
        const video = videos[index];
        if (!video) return;
        let videoUrl;
        if (video.youtubeId)
          videoUrl = `https://www.youtube.com/embed/${video.youtubeId}`;
        else if (video.vimeoId)
          videoUrl = `https://player.vimeo.com/video/${video.vimeoId}`;
        return videoUrl && <Video key={index} src={videoUrl} />;
      }}
    </Slides>
  ),
  total: videos.length,
  withControls: videos.length > 1,
  thumbnails,
  navigation: {
    text: T('listings.media.video'),
    icon: <VideoPlayerSliderBold data-test-locator="Gallery/Video" />,
  },
  name: GalleryContentType.Video,
});

const GalleryLayout: React.FC<Props> = ({
  photos,
  floorplanPhotos,
  panoramicPhotos,
  videos,
  isFullscreenGalleryOpen,
  initialSlideIndex,
  initialContentName,
  onClose,
  onImageButtonClick,
  onPanoramicPhotosButtonClick,
  onVideoButtonClick,
  onFloorplanButtonClick,
  onMapButtonClick,
  onVirtualTourButtonClick,
  street,
  country,
  city,
  listingId,
  imageGridProps,
  hasCoordinates,
  coordinates,
  description,
}) => {
  const { T } = useIntl();
  const { md } = useMediaQuery();
  const currentScreenSize = useCurrentScreenSize();
  const { transformImageURL } = useImgix();
  const virtualTourLink = getMatterportLink(description);
  const videoItems = videos.filter(v => v.youtubeId || v.vimeoId);
  const transformedPhotos = React.useMemo(
    () =>
      photos.map(image => ({
        ...image,
        url: transformImageURL(image.url, {
          ...GalleryImgixSizeByScreenSize[currentScreenSize],
          fit: 'clamp',
        }),
      })),
    [currentScreenSize, photos, transformImageURL],
  );

  const transformedPhotosThumbnails = React.useMemo(
    () =>
      photos.map(image =>
        transformImageURL(image.url, {
          ...ImgixSize.xsmall,
          fit: 'clamp',
        }),
      ),
    [photos, transformImageURL],
  );

  const transformedFloorplanPhotos = React.useMemo(
    () =>
      floorplanPhotos.map(image => ({
        ...image,
        url: transformImageURL(image.url, {
          ...GalleryImgixSizeByScreenSize[currentScreenSize],
          fit: 'clamp',
        }),
      })),
    [currentScreenSize, floorplanPhotos, transformImageURL],
  );

  const transformedFloorplanPhotosThumbnails = React.useMemo(
    () =>
      floorplanPhotos.map(image =>
        transformImageURL(image.url, {
          ...ImgixSize.xsmall,
          fit: 'clamp',
        }),
      ),
    [floorplanPhotos, transformImageURL],
  );

  const transformedPanoramicPhotos = React.useMemo(
    () =>
      panoramicPhotos.map(image => ({
        ...image,
        url: transformImageURL(image.url, {
          h: GalleryImgixSizeByScreenSize[currentScreenSize].w,
          w: 2 * GalleryImgixSizeByScreenSize[currentScreenSize].w,
          fit: 'clip',
        }),
      })),
    [currentScreenSize, panoramicPhotos, transformImageURL],
  );

  const transformedPanoramicPhotosThumbnails = React.useMemo(
    () =>
      panoramicPhotos.map(image =>
        transformImageURL(image.url, {
          ...ImgixSize.xsmall,
          fit: 'clamp',
        }),
      ),
    [panoramicPhotos, transformImageURL],
  );

  const content = React.useMemo(() => {
    const result = [];

    if (transformedPhotos.length > 0) {
      result.push(
        getPhotosContent(T, transformedPhotos, transformedPhotosThumbnails, md),
      );
    }

    if (transformedPanoramicPhotos.length > 0) {
      result.push(
        getPanoramicPhotosContent(
          T,
          transformedPanoramicPhotos,
          transformedPanoramicPhotosThumbnails,
        ),
      );
    }

    if (virtualTourLink) {
      result.push(getVirtualTourContent(T, virtualTourLink));
    }

    if (videoItems.length > 0) {
      result.push(getVideosContent(T, videoItems, []));
    }

    if (transformedFloorplanPhotos.length > 0) {
      result.push(
        getFloorplansContent(
          T,
          transformedFloorplanPhotos,
          transformedFloorplanPhotosThumbnails,
          md,
        ),
      );
    }

    if (hasCoordinates) {
      result.push(getMapContent(T, coordinates));
    }

    return result;
  }, [
    transformedPhotos,
    transformedPanoramicPhotos,
    virtualTourLink,
    videoItems,
    transformedFloorplanPhotos,
    hasCoordinates,
    T,
    transformedPhotosThumbnails,
    md,
    transformedPanoramicPhotosThumbnails,
    transformedFloorplanPhotosThumbnails,
    coordinates,
  ]);

  const layoutProps = {
    photos,
    panoramicPhotos,
    floorplanPhotos,
    onImageButtonClick,
    onPanoramicPhotosButtonClick,
    onVideoButtonClick,
    onFloorplanButtonClick,
    onMapButtonClick,
    onVirtualTourButtonClick,
    virtualTourLink,
    street,
    country,
    city,
    listingId,
    numVideos: videoItems.length,
    imageGridProps,
    hasCoordinates,
    showDefaultMediaType: !photos.length && !floorplanPhotos.length,
  };

  return (
    <React.Fragment>
      <Dialog open={isFullscreenGalleryOpen} onClose={onClose} fullScreen>
        <Gallery
          onClose={onClose}
          content={content}
          initialSlideIndex={initialSlideIndex}
          initialContentName={initialContentName}
        />
      </Dialog>

      {md ? (
        <GalleryDesktopLayout {...layoutProps} />
      ) : (
        <GalleryMobileLayout {...layoutProps} />
      )}
    </React.Fragment>
  );
};

export { GalleryLayout };
